import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
} from '@angular/core';
import {
    CustomPresetValue,
    DateRangePresetId,
    DateSplitAggregate,
    DateSplitAggregates,
    IDataRangePickerData,
    NamedPresetValue,
    defaultRangePresetIds,
} from 'src/app/components/date-picker/date-range-picker';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
    IQueryContext,
    IQueryContextChange,
    IQueryContextDatasourceColumnState,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    QueryContextChange,
    QueryContextService,
} from 'src/app/services/query-context/query-context.service';
import {
    IDashboardItemFactory,
    IDatasourceColumnDashboardItem,
} from 'src/app/utils/dashboard-item';
import {
    DatasourceColumnId,
    getDatasourceColumnName,
} from 'src/app/utils/datasource-sql-map/datasource';
import { NotImplementedError } from 'src/app/utils/error';
import { sqlDateTrunc } from 'src/app/utils/sql';
import { CustomRangeHelper } from './custom-range-helper';
import { is } from 'date-fns/locale';
import { AfterMethod } from 'src/app/utils/decorators/after-method';

export interface IDateRangePickerDashboardItemState
    extends IQueryContextDatasourceColumnState {
    value: CustomPresetValue | NamedPresetValue;
    isFilterApplied: boolean;
    isSplitApplied: boolean;
}

export interface IDateRangePickerDashboardItem
    extends IDatasourceColumnDashboardItem {
    // override default shape of 'state' object
    datasourceColumn: {
        id: DatasourceColumnId;
        state: IDateRangePickerDashboardItemState;
    };
    // override default return value of compoData function
    composeData: (a: any[], b?: IQueryContext) => IDataRangePickerData;
    // add new 'options' object
    options: {
        /** Whether to show control checkbox and split range select dropdown */
        splitable?: boolean;
        /** Control checkbox is not shown, but split range select dropdown is shown. Assumes that split is always applied */
        forcedSplitable?: boolean;
        /** Controls the available split aggregates in the split range select dropdown */
        spltAggregates?: DateSplitAggregate[];
        /** Presets to show in the range select dropdown */
        rangePresets?: DateRangePresetId[];
    };
}

// Define the available units as a const array and derive the type from it
export const DATE_RANGE_RELATIVE_UNITS = [
    'days ago',
    'weeks ago',
    'months ago',
    'years ago',
] as const;

export type RelativeDateRangeUnit = (typeof DATE_RANGE_RELATIVE_UNITS)[number];

export interface RelativeDateRangeBound {
    // Represents the number of units in the relative date range
    unitCount: number;
    // Represents the unit of time for the relative date range (e.g., days, weeks, months, years)
    unit: RelativeDateRangeUnit;
}

// Component for selecting a date range with various presets and options
// It allows users to select a date range, apply splits, and choose date aggregation options
@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent<D> implements OnInit {
    // Date format used in the ant.d specific date picker
    readonly DATE_FORMAT = 'dd.MM.yyyy';
    // Available units for selecting relative date ranges
    readonly RELATIVE_UNITS = DATE_RANGE_RELATIVE_UNITS;

    // Available date aggregation options for the split functionality
    DATE_SPLIT_AGGREGATES!: DateSplitAggregate[];

    // Available date range presets for quick selection
    rangePresetIds!: Array<DateRangePresetId>;

    dashboardItemState: IDateRangePickerDashboardItemState =
        DatePickerComponent.getDefaultState();

    @Input()
    dashboardItemFactory!: IDashboardItemFactory;
    dashboardItem!: IDateRangePickerDashboardItem;

    @Input()
    dataLoaderEmitter!: EventEmitter<IDataLoaderResult>;

    // Represents the selected date range preset
    // IT CAN BE SET ONLY FROM WITHIN UpdateDateValue method!!!
    selectedPresetModel: DateRangePresetId = 'lifetime';
    isSplitAppliedModel: boolean = false;
    startDateModel?: Date = undefined;
    endDateModel?: Date = undefined;
    dateAggModel: DateSplitAggregate = 'day';

    // Models for relative fields
    relativeStartUnitCountModel?: number = undefined;
    relativeStartUnitModel: RelativeDateRangeUnit = 'days ago';

    relativeEndUnitCountModel?: number = undefined;
    relativeEndUnitModel: RelativeDateRangeUnit = 'days ago';

    // Reason why the period cannot be shifted
    // This is used to display a message when the user tries to shift the period but it cannot be done
    cannotShiftPeriodReason: string = '';

    constructor(
        private queryContextService: QueryContextService,
        private cd: ChangeDetectorRef,
        private logger: LoggerService
    ) {}

    ngOnInit(): void {
        this.dashboardItem =
            this.dashboardItemFactory.create() as IDateRangePickerDashboardItem;

        // Subscribe to the query context service to get every change in the query context
        this.queryContextService.appliedQueryContextEmitter.subscribe(
            (contextChange) => this.queryContextSubscriber(contextChange)
        );

        // Replace default rangePresets with the ones provided in the options
        this.rangePresetIds =
            this.dashboardItem.options.rangePresets ??
            defaultRangePresetIds.slice();

        this.DATE_SPLIT_AGGREGATES = this.dashboardItem.options
            .spltAggregates ?? ['day', 'week', 'month', 'quarter', 'year'];
    }

    private queryContextSubscriber(contextChange: QueryContextChange): void {
        if (contextChange.reason === 'InitialContext') {
            // find in contextChange.context.datasourceColumns item with the same id as this.datasourceColumnId
            const datasourceColumn =
                contextChange.context.datasourceColumns.find((item) => {
                    return item.id === this.dashboardItem.datasourceColumnId;
                });
            // if found, set state and other properties otherwise set default state
            const state = datasourceColumn
                ? datasourceColumn.state
                : DatePickerComponent.getDefaultState();
            this.setupUIModelsFromQueryContextState(state);

            this.cd.detectChanges();
        }
    }

    private setupUIModelsFromQueryContextState(
        state: IDateRangePickerDashboardItemState
    ) {
        this.dashboardItemState = state;
        this.dateAggModel = this.dashboardItemState.value.splitAggregate;
        this.isSplitAppliedModel = this.dashboardItemState.isSplitApplied;
        this.selectedPresetModel = this.dashboardItemState.value.preset;

        if (this.dashboardItemState.value.preset === 'custom') {
            this.startDateModel =
                'start' in this.dashboardItemState.value
                    ? this.dashboardItemState.value.start
                    : undefined;
            this.endDateModel =
                'end' in this.dashboardItemState.value
                    ? this.dashboardItemState.value.end
                    : undefined;

            // If custom, try to infer relative fields from start/end
            let { unitCount, unit } =
                'relativeStart' in this.dashboardItemState.value
                    ? this.dashboardItemState.value.relativeStart!
                    : ({ unitCount: undefined, unit: 'days ago' } as const);
            this.relativeStartUnitCountModel = unitCount;
            this.relativeEndUnitModel = unit;

            if (
                'relativeStart' in this.dashboardItemState.value &&
                this.dashboardItemState.value.relativeStart !== undefined
            ) {
                this.startDateModel =
                    DatePickerComponent.calculateDateFromRelative(
                        this.dashboardItemState.value.relativeStart
                    );
            }
            ({ unitCount, unit } =
                'relativeEnd' in this.dashboardItemState.value
                    ? this.dashboardItemState.value.relativeEnd!
                    : ({ unitCount: undefined, unit: 'days ago' } as const));
            this.relativeEndUnitCountModel = unitCount;
            this.relativeEndUnitModel = unit;
            if (
                'relativeEnd' in this.dashboardItemState.value &&
                this.dashboardItemState.value.relativeEnd !== undefined
            ) {
                this.endDateModel =
                    DatePickerComponent.calculateDateFromRelative(
                        this.dashboardItemState.value.relativeEnd
                    );
            }
        } else if (this.dashboardItemState.value.preset !== 'lifetime') {
            // assign start and end based on preset
            const [start, end] = CustomRangeHelper.calculateDateRange(
                this.dashboardItemState.value.preset
            );
            this.startDateModel = start;
            this.endDateModel = end;
            // Calculate relative fields from preset
            this.setRelativeModelsFromPreset(
                this.dashboardItemState.value.preset
            );
        } else {
            // lifetime preset
            this.startDateModel = undefined;
            this.endDateModel = undefined;
            this.relativeStartUnitCountModel = undefined;
            // this.relativeStartUnitModel = 'days ago';
            this.relativeEndUnitCountModel = undefined;
            // this.relativeEndUnitModel = 'days ago';
        }
    }

    /**
     * Called when the user changes the date range preset.
     * It updates the start and end dates based on the selected preset.
     * @param preset
     */
    @AfterMethod((self) => self.updatePendingQueryContextFromUIModels())
    onUIPresetChange(preset: DateRangePresetId) {
        this.logger.debug('DatePickerComponent.onPresetChange', {
            id: this.dashboardItem.datasourceColumnId,
            preset,
        });
        let value: IDateRangePickerDashboardItemState['value'] = {
            ...this.dashboardItemState.value,
            preset: preset,
        };
        // if 'lifetime' preset is selected set start and end to null
        switch (preset) {
            case 'lifetime': {
                this.startDateModel = undefined;
                this.endDateModel = undefined;
                this.relativeStartUnitCountModel = undefined;
                // this.relativeStartUnitModel = 'days ago';
                this.relativeEndUnitCountModel = undefined;
                // this.relativeEndUnitModel = 'days ago';
                // Update the state with the new value
                value = {
                    preset: 'lifetime',
                    splitAggregate:
                        this.dashboardItemState.value.splitAggregate,
                };
                break;
            }
            case 'custom': {
                // Update the state with the new value
                // const value = {
                //     preset: 'custom',
                //     start: this.startDateModel,
                //     end: this.endDateModel,
                //     relativeStart: this.relativeStartModel,
                //     relativeEnd: this.relativeEndModel,
                //     splitAggregate:
                //         this.dashboardItemState.value.splitAggregate,
                // };
                break;
            }
            // TODO presets should not be hardcoded but rather based on Date Dimension in BigQuery
            // presets other than 'lifetime' and 'custom'
            default: {
                const [start, end] =
                    CustomRangeHelper.calculateDateRange(preset);
                this.startDateModel = start;
                this.endDateModel = end;
                this.setRelativeModelsFromPreset(preset);
                // Update the state with the new value
                const value = {
                    preset: preset,
                    splitAggregate:
                        this.dashboardItemState.value.splitAggregate,
                };
            }
        }
    }

    /**
     * Called when user changes the start date in the date picker.
     *
     * @param startDate - The new start date to be set
     */
    @AfterMethod((self) => self.updatePendingQueryContextFromUIModels())
    onUIDateChange(type: 'start' | 'end', startDate: Date | undefined) {
        if (type === 'start') {
            this.startDateModel = startDate ?? undefined;
            this.relativeStartUnitCountModel = undefined;
        }
        if (type === 'end') {
            this.endDateModel = startDate ?? undefined;
            this.relativeEndUnitCountModel = undefined;
        }
        // this.updateDateValue(startDate, this.endDateModel);
        // Get the appropriate value based on the provided dates
        const newPresetValue = this.buildDateRangeStateValue(
            this.startDateModel,
            this.endDateModel
        );

        // Update the preset model based on the determined preset from the value
        this.selectedPresetModel = newPresetValue.preset;
    }

    /**
     * Called when the user changes the relative start or end field.
     * Updates the absolute start/end dates and sets preset to 'custom'.
     */
    @AfterMethod((self) => self.updatePendingQueryContextFromUIModels())
    onUIRelativeChange(
        type: 'start' | 'end',
        unitCount: number | undefined,
        unit: RelativeDateRangeUnit
    ) {
        if (unitCount === undefined) {
            // Do nothing if unitCount is undefined
            return;
        }
        if (type === 'start') {
            this.relativeStartUnitCountModel = unitCount;
            this.relativeStartUnitModel = unit;
            this.selectedPresetModel = 'custom';
            this.startDateModel = DatePickerComponent.calculateDateFromRelative(
                {
                    unitCount: unitCount,
                    unit: unit,
                }
            );
        } else {
            this.relativeEndUnitCountModel = unitCount;
            this.relativeEndUnitModel = unit;
            this.selectedPresetModel = 'custom';
            this.endDateModel = DatePickerComponent.calculateDateFromRelative({
                unitCount: unitCount,
                unit: unit,
            });
        }
    }

    /**
     * Shifts the current period to the next or previous period of the same length.
     * The resulting preset is always 'custom'.
     * @param direction 'next' or 'previous'
     */
    @AfterMethod((self) => self.updatePendingQueryContextFromUIModels())
    onUIShiftPeriod(direction: 'next' | 'previous') {
        if (!this.startDateModel || !this.endDateModel) {
            // If either date is missing, do nothing
            return;
        }
        const start = this.startDateModel;
        const end = this.endDateModel;
        // Calculate the number of days in the period (inclusive)
        const msInDay = 24 * 60 * 60 * 1000;
        const periodDays =
            Math.round((end.getTime() - start.getTime()) / msInDay) + 1;
        const offset = periodDays * msInDay * (direction === 'next' ? 1 : -1);
        const newStart = new Date(start.getTime() + offset);
        const newEnd = new Date(end.getTime() + offset);
        this.startDateModel = newStart;
        this.endDateModel = newEnd;
        this.relativeStartUnitCountModel = undefined;
        this.relativeEndUnitCountModel = undefined;
        const newPresetValue = this.buildDateRangeStateValue(
            this.startDateModel,
            this.endDateModel
        );

        // Update the preset model based on the determined preset from the value
        this.selectedPresetModel = newPresetValue.preset;
    }

    @AfterMethod((self) => self.updatePendingQueryContextFromUIModels())
    onUIDateAggChange(dateAgg: any) {
        this.dashboardItemState = {
            ...this.dashboardItemState,
            value: {
                ...this.dashboardItemState.value,
                splitAggregate: dateAgg,
            },
        };
    }

    @AfterMethod((self) => self.updatePendingQueryContextFromUIModels())
    onUISplitChange(value: boolean) {
        // console.log({
        //     msg: 'DatePickerComponent.onSplitChange',
        //     filterId: this.dashboardItem.datasourceColumnId,
        //     newValue: value,
        // });
        this.isSplitAppliedModel = value;
    }

    /**
     * Set relativeStartModel and relativeEndModel based on preset string (e.g. 'last 365 days')
     */
    private setRelativeModelsFromPreset(preset: DateRangePresetId) {
        // Example: 'last 365 days' => start: 365 days ago, end: 0 days ago
        const match = /^last (\d+) (days|weeks|months|years)$/.exec(preset);
        if (match) {
            const count = parseInt(match[1], 10);
            const unit = (match[2] + ' ago') as RelativeDateRangeUnit;
            this.relativeStartUnitCountModel = count;
            this.relativeStartUnitModel = unit;
            this.relativeEndUnitCountModel = 0;
            this.relativeEndUnitModel = unit;
        } else if (preset === 'yesterday') {
            this.relativeStartUnitCountModel = 1;
            this.relativeStartUnitModel = 'days ago';
            this.relativeEndUnitCountModel = 1;
            this.relativeEndUnitModel = 'days ago';
        } else if (preset === 'today') {
            this.relativeStartUnitCountModel = 0;
            this.relativeStartUnitModel = 'days ago';
            this.relativeEndUnitCountModel = 0;
            this.relativeEndUnitModel = 'days ago';
        } else {
            this.relativeStartUnitCountModel = undefined;
            // this.relativeStartUnitModel = 'days ago';
            this.relativeEndUnitCountModel = undefined;
            // this.relativeEndUnitModel = 'days ago';
            throw new NotImplementedError(
                `Relative date range bounds for preset "${preset}" are not implemented`
            );
        }
    }

    /**
     * This method is called to update the query context based on the current UI models.
     * It updates the date value in the state and emits the change through the query context service.
     */
    private updatePendingQueryContextFromUIModels() {
        const queryContextValue: CustomPresetValue | NamedPresetValue = {
            preset: this.selectedPresetModel,
            splitAggregate: this.dateAggModel,
        };

        // If the preset is 'custom', include start and end dates
        if (queryContextValue.preset === 'custom') {
            if (this.relativeStartUnitCountModel !== undefined) {
                queryContextValue.relativeStart = {
                    unitCount: this.relativeStartUnitCountModel,
                    unit: this.relativeStartUnitModel,
                };
            }
            if (this.relativeEndUnitCountModel !== undefined) {
                queryContextValue.relativeEnd = {
                    unitCount: this.relativeEndUnitCountModel,
                    unit: this.relativeEndUnitModel,
                };
            }
            // If relative start or end is not set, set the absolute dates
            if (
                this.relativeStartUnitCountModel === undefined &&
                this.startDateModel !== undefined
            ) {
                queryContextValue.start = this.startDateModel;
            }
            if (
                this.relativeEndUnitCountModel === undefined &&
                this.endDateModel !== undefined
            ) {
                queryContextValue.end = this.endDateModel;
            }
        }

        const state = {
            ...this.dashboardItemState,
            value: queryContextValue,
            isFilterApplied:
                DatePickerComponent.canFilterApply(queryContextValue),
            isSplitApplied: this.isSplitAppliedModel,
        };

        this.dashboardItemState = state;

        // Construct the context change object for the query context service
        const contextChange: IQueryContextChange = {
            datasourceColumn: {
                id: this.dashboardItem.datasourceColumnId,
                state,
            },
        };

        // Emit the context change
        this.queryContextService.changePendingQueryContext(contextChange);
    }

    /**
     * Method to update the date value in the state and emit necessary changes.
     * It first determines the appropriate value using buildDateRangeStateValue and then updates the state.
     * Finally, it emits the change through the query context service.
     *
     * @param startDate - The start date to be considered for updating the value
     * @param endDate - The end date to be considered for updating the value
     */
    private updateDateValue(startDate?: Date, endDate?: Date) {
        // Get the appropriate value based on the provided dates
        const presetValue = this.buildDateRangeStateValue(startDate, endDate);

        // Update the preset model based on the determined preset from the value
        this.selectedPresetModel = presetValue.preset;

        // // Update the state with the new value
        // const state = {
        //     ...this.dashboardItemState,
        //     value: presetValue,
        //     isFilterApplied: DatePickerComponent.canFilterApply(presetValue),
        // };
        // this.dashboardItemState = state;

        // // Construct the context change object for the query context service
        // const contextChange: IQueryContextChange = {
        //     datasourceColumn: {
        //         id: this.dashboardItem.datasourceColumnId,
        //         state,
        //     },
        // };

        // // Emit the context change
        // this.queryContextService.changePendingQueryContext(contextChange);
    }

    /**
     * This method determines the appropriate value based on the provided start and end dates.
     * If both start and end dates are undefined, it defaults to a 'lifetime' preset.
     * Otherwise, it constructs a 'custom' preset value based on the provided dates.
     *
     * @param startDate - The start date to be considered
     * @param endDate - The end date to be considered
     * @returns The constructed value based on the given dates. 'lifetime' preset if both dates are undefined, or 'custom' preset with the provided dates.
     */
    private buildDateRangeStateValue(
        startDate?: Date,
        endDate?: Date
    ): IDateRangePickerDashboardItemState['value'] {
        // Default to lifetime if both dates are not provided
        if (!startDate && !endDate) {
            return {
                preset: 'lifetime',
                splitAggregate: this.dashboardItemState.value.splitAggregate,
            };
        } else {
            // either start or end is set
            // if start is set but end is not set
            if (startDate && !endDate) {
                return {
                    preset: 'custom',
                    start: startDate,
                    splitAggregate:
                        this.dashboardItemState.value.splitAggregate,
                };
            }
            // if end is set but start is not set
            else if (!startDate && endDate) {
                return {
                    preset: 'custom',
                    end: endDate,
                    splitAggregate:
                        this.dashboardItemState.value.splitAggregate,
                };
            }
            // if both start and end are set
            else {
                return {
                    preset: 'custom',
                    start: startDate,
                    end: endDate,
                    splitAggregate:
                        this.dashboardItemState.value.splitAggregate,
                };
            }
        }
    }

    /**
     * Calculate absolute date from relative value (e.g. 10 days ago from today)
     */
    public static calculateDateFromRelative(
        bound: RelativeDateRangeBound
    ): Date {
        const now = new Date();
        switch (bound.unit) {
            case 'days ago':
                return new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate() - bound.unitCount
                );
            case 'weeks ago':
                return new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate() - bound.unitCount * 7
                );
            case 'months ago':
                return new Date(
                    now.getFullYear(),
                    now.getMonth() - bound.unitCount,
                    now.getDate()
                );
            case 'years ago':
                return new Date(
                    now.getFullYear() - bound.unitCount,
                    now.getMonth(),
                    now.getDate()
                );
            // 'fixed' should not be passed here, but if it is, just return now
            default:
                return now;
        }
    }

    /**
     * Returns true if the shift period buttons should be enabled (both start and end dates are set).
     */
    canShiftPeriod(): boolean {
        const canShift = !!this.startDateModel && !!this.endDateModel;
        this.cannotShiftPeriodReason = canShift
            ? ''
            : 'Please select a date range first.';
        return canShift;
    }

    /**
     * Returns true if the shift relative period buttons should be enabled.
     */
    canShiftRelativePeriod(): boolean {
        // TODO implement logic to determine if the period can be shifted
        // Can shift relative period if selected relative unit is not 'date'
        return true;
    }

    // public clearDataInput() {
    //     // this.formGroup.reset();
    //     // this.picker.close();
    //     // console.log('Clear data input');
    // }

    public getColumnLabel() {
        return (
            this.dashboardItem?.datasourceColumnLabel ??
            getDatasourceColumnName(this.dashboardItem.datasourceColumnId) ??
            this.dashboardItem.datasourceColumnId
        );
    }

    public static getDefaultState(): IDateRangePickerDashboardItemState {
        return {
            value: { preset: 'lifetime', splitAggregate: 'day' },
            isFilterApplied: false,
            isSplitApplied: false,
        };
    }

    public static toFilterSQL(
        columnName: string,
        value: IDateRangePickerDashboardItem['datasourceColumn']['state']['value']
    ) {
        // console.log({
        //     msg: 'DatePickerComponent.toSQL',
        //     columnName,
        //     value: value,
        // });
        const lastEtlDateSql =
            '(SELECT MAX(first_open_date) FROM am-lastarchon-dev.analytics.users_firebase_dimensions)';
        // if preset 'lifetime' is selected throw error
        // lifetime preset means that start and end are not set and filter should not be applied
        if (value.preset == 'lifetime') {
            throw Error('Invalid value error. Lifetime preset not supported');
        }
        // if preset is set nothing else is taken into account
        if (value.preset != 'custom') {
            switch (value.preset) {
                case 'today':
                case 'yesterday':
                case 'last 7 days':
                case 'last 14 days':
                case 'last 30 days':
                case 'last 60 days':
                case 'last 90 days':
                case 'last 180 days':
                case 'last 365 days':
                case 'last 540 days':
                case 'last 720 days':
                    const [start, end] = CustomRangeHelper.calculateDateRange(
                        value.preset
                    );
                    return `${columnName} >= '${start
                        .toISOString()
                        .substring(0, 10)}' AND ${columnName} <= '${end
                        .toISOString()
                        .substring(0, 10)}'`;

                default:
                    throw Error(
                        'Invalid value error. Provided preset value not supported'
                    );
            }
        } else {
            const sql = Object.keys(value)
                .map((k) => {
                    const format = (date: Date) => {
                        const YYYY = date.getFullYear();
                        const MM = ('0' + (date.getMonth() + 1)).slice(-2);
                        const DD = ('0' + date.getDate()).slice(-2);
                        return `${YYYY}-${MM}-${DD}`;
                    };
                    // console.log({
                    //     msg: 'DatePickerComponent.toSQL.internal',
                    //     k,
                    //     value,
                    //     value_start: value.start,
                    //     value_end: value.end,
                    // });
                    if (k == 'start' && value.start) {
                        return `${columnName} >= '${format(value.start)}'`;
                    }
                    if (k == 'end' && value.end) {
                        return `${columnName} <= '${format(value.end)}'`;
                    }
                    if (k == 'relativeStart' && value.relativeStart) {
                        // relative start is a bound, so we need to calculate the date
                        const relativeDate = this.calculateDateFromRelative(
                            value.relativeStart
                        );
                        return `${columnName} >= '${format(relativeDate)}'`;
                    }
                    if (k == 'relativeEnd' && value.relativeEnd) {
                        // relative end is a bound, so we need to calculate the date
                        const relativeDate = this.calculateDateFromRelative(
                            value.relativeEnd
                        );
                        return `${columnName} <= '${format(relativeDate)}'`;
                    }
                    return;
                })
                .filter((v) => v)
                .join(' AND ');
            // console.log({
            //     msg: 'DatePickerComponent.toSQL',
            //     columnName,
            //     value: value,
            //     sql,
            // });
            return sql;
        }
    }

    public static toSplitSQL(
        columnName: string,
        value: IDateRangePickerDashboardItem['datasourceColumn']['state']['value']
    ) {
        // console.log({
        //     msg: 'DatePickerComponent.toSplitSQL',
        //     columnName,
        //     value: value,
        // });
        let dateTrunc = columnName;
        switch (value.splitAggregate) {
            case 'day':
                // No truncation needed for 'day'
                break;
            case 'week':
            case 'month':
            case 'quarter':
            case 'year':
                dateTrunc = sqlDateTrunc(columnName, value.splitAggregate);
                break;
            case 'total':
                dateTrunc = `DATE('2000-01-01')`;
                // No truncation needed for 'total'
                break;
            default:
                const exhaustiveCheck: never = value.splitAggregate;
                throw new Error(
                    `Unhandled splitAggregate: ${value.splitAggregate}`
                );
        }
        return `CAST(${dateTrunc} AS STRING)`;
    }

    static canFilterApply(
        value: IDateRangePickerDashboardItem['datasourceColumn']['state']['value']
    ): boolean {
        let returnVal = true;
        // return false if 'lifetime' preset is selected
        if (value.preset == 'lifetime') {
            returnVal = false;
        }
        // return false if 'custom' preset but all start, end, relativeStart, relativeEnd  are not set
        else if (
            value.preset == 'custom' &&
            !value.start &&
            !value.end &&
            !value.relativeStart &&
            !value.relativeEnd
        ) {
            returnVal = false;
        } else {
            returnVal = true;
        }
        // console.log({
        //     msg: 'DatePickerComponent.canFilterApply',
        //     value: value,
        //     return: returnVal,
        // });
        return returnVal;
    }
}
